import type { ChatResponseStatus } from "@/components/ai-chat/types";

import { PAGE_ASSISTANT_CHAT_TYPES } from "@/lib/page-assistant-threads";

import type { ChatHistoryEntry, ChatThreadSummary } from "@/types/chat-thread";

import { requestData } from "@/api/api-config";

/** Ask Decoda's own chats plus every page assistant's, so all show in one list. */
export const ASK_DECODA_THREAD_CHAT_TYPES = [
  "PLATFORM_AGENT",
  "ANALYTICS",
  "DOCS_QA",
  ...PAGE_ASSISTANT_CHAT_TYPES,
];

/** Shared SQL history source is retained only for non-Ask page assistants. */
export const PAGE_ASSISTANT_THREAD_CHAT_TYPES = [...PAGE_ASSISTANT_CHAT_TYPES];

/** Tenant-scoped React Query key for Ask Decoda thread summaries. */
export const askDecodaThreadsQueryKey = (tenantSubDomain?: string) =>
  [
    "ai",
    "chat-threads",
    tenantSubDomain ?? "no-tenant",
    ...ASK_DECODA_THREAD_CHAT_TYPES,
  ] as const;

/** How long thread history stays fresh for Ask Decoda navigation. */
export const ASK_DECODA_THREAD_DETAIL_STALE_MS = 30_000;

export const askDecodaThreadHistoryQueryKey = (
  tenantSubDomain: string | undefined,
  threadId: string,
) => ["ai", "chat-history", tenantSubDomain ?? "no-tenant", threadId] as const;

/** Product annotations/read state have a cache lifecycle independent of checkpoints. */
export const askDecodaProductMetadataQueryKey = (
  tenantSubDomain: string | undefined,
  threadId: string,
) =>
  [
    "ask-decoda",
    "product-metadata",
    tenantSubDomain ?? "no-tenant",
    threadId,
  ] as const;

export type { ChatHistoryEntry, ChatThreadSummary } from "@/types/chat-thread";

export async function getChatThreads(
  chatTypes: readonly string[],
): Promise<ChatThreadSummary[]> {
  const params = new URLSearchParams();
  for (const t of chatTypes) {
    params.append("chat_type", t);
  }
  const result = await requestData<ChatThreadSummary[]>(
    `ai/chat-history/threads?${params.toString()}`,
    "get",
  );
  return result ?? [];
}

export async function getNativeAskDecodaThreads(): Promise<
  ChatThreadSummary[]
> {
  const result = await requestData<ChatThreadSummary[]>(
    "agent/ask-decoda/conversations",
    "get",
  );
  return result ?? [];
}

/** Keep older Ask Decoda chats visible alongside native and page-assistant chats. */
export async function getAskDecodaThreads(): Promise<ChatThreadSummary[]> {
  const [nativeThreads, pageAssistantThreads] = await Promise.all([
    getNativeAskDecodaThreads(),
    getChatThreads(ASK_DECODA_THREAD_CHAT_TYPES),
  ]);
  const nativeIds = new Set(nativeThreads.map((thread) => thread.threadId));
  const historicalThreads = pageAssistantThreads
    .filter((thread) => !nativeIds.has(thread.threadId))
    .map((thread) => ({
      ...thread,
      isLegacy:
        thread.isLegacy ||
        thread.chatType === "PLATFORM_AGENT" ||
        thread.chatType === "ANALYTICS" ||
        thread.chatType === "DOCS_QA",
    }));
  return [...nativeThreads, ...historicalThreads]
    .toSorted(
      (a, b) =>
        new Date(b.latestActivity).getTime() -
          new Date(a.latestActivity).getTime() ||
        (a.threadId < b.threadId ? 1 : a.threadId > b.threadId ? -1 : 0),
    )
    .slice(0, 20);
}

export async function migrateAskDecodaChat(threadId: string) {
  return requestData<{ threadId: string }>(
    `agent/ask-decoda/conversations/${threadId}/migrate`,
    "post",
    {},
    false,
  );
}

export async function getNativeAskDecodaHistory(
  threadId: string,
  options?: { signal?: AbortSignal },
): Promise<ChatHistoryEntry[]> {
  const result = await requestData<ChatHistoryEntry[]>(
    `agent/ask-decoda/conversations/${threadId}`,
    "get",
    options,
    false,
  );
  return result ?? [];
}

export async function setNativeAskDecodaReadState(
  threadId: string,
  isRead: boolean,
): Promise<{
  threadId: string;
  isRead: boolean;
  responseId: string | null;
  readAt: string | null;
} | null> {
  return requestData(
    `agent/ask-decoda/conversations/${threadId}/read`,
    "put",
    { json: { isRead } },
    false,
  );
}

export async function setNativeAskDecodaFeedback(
  threadId: string,
  responseId: string,
  feedbackType: ChatHistoryEntry["feedbackType"],
  feedbackText?: string | null,
): Promise<{
  responseId: string;
  feedbackType: ChatHistoryEntry["feedbackType"];
} | null> {
  return requestData(
    `agent/ask-decoda/conversations/${threadId}/messages/${responseId}/feedback`,
    "put",
    { json: { feedbackType, feedbackText } },
    false,
  );
}

export async function setChatThreadReadState(
  threadId: string,
  isRead: boolean,
): Promise<{ readAt: string | null } | null> {
  return requestData(
    `ai/chat-history/threads/${threadId}/read`,
    "put",
    { json: { isRead } },
    false,
  );
}

export async function resolveChatThreadBlock(
  threadId: string,
  chatHistoryId: string,
): Promise<{ status: ChatResponseStatus; readAt: string | null } | null> {
  return requestData(
    `ai/chat-history/threads/${threadId}/resolve-block`,
    "put",
    { json: { chatHistoryId } },
    false,
  );
}

export async function getChatHistory(
  threadId: string,
  options?: { signal?: AbortSignal },
): Promise<ChatHistoryEntry[]> {
  const result = await requestData<ChatHistoryEntry[]>(
    `ai/chat-history/${threadId}`,
    "get",
    options,
  );
  return result ?? [];
}
